<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "../../../../assets/css/_colors.scss" as *;
  @use "../../../../assets/css/_mixins.scss" as *;
  .heatmapGrid {
    width: 100%;
    display: grid;
    grid-template-columns: auto repeat(24, 1fr);
    grid-auto-rows: 1fr auto;

    .dayLabel {
      align-items: center;
      border-right: 1px solid $light-gray;
      display: flex;
      padding-right: 0.5rem;
      justify-content: flex-end;
      @extend .text-body-4;
    }

    .hourLabel {
      margin-top: 0.5rem;
      text-align: center;
      @extend .text-body-4;
    }

    .cell {
      width: 100%;
      border-right: 1px solid $light-gray;
      border-bottom: 1px solid $light-gray;
      position: relative;
      padding-top: 100%;

      // TODO - confirm with product that this outline transition is desired
      &.notNull {
        transition:
          box-shadow 0.25s,
          border-radius 0.25s;

        &:hover {
          outline: none;
          box-shadow: 0 0 0 2px #44d5c8;
          z-index: 1;
          border-radius: 2px;
        }
      }

      &:nth-last-child(-n + 24) {
        border-bottom: none;
      }

      &:nth-child(-n + 25) {
        border-top: 1px solid $light-gray;
      }

      &::before {
        content: "";
        border-radius: 2px;
        @include absolute-center;
        @include size(85%);
        background-color: var(--cell-color, transparent);
      }

      .cellTooltipContent {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        h3 {
          @extend .text-body-2-bold;
          line-height: 10%;
        }

        .subLine {
          display: flex;
          justify-content: space-between;
          @extend .text-body-2;

          .text {
            align-items: center;
            display: flex;
            gap: 0.375rem;

            .icon {
              background-color: var(--cell-color, transparent);
              border-radius: 2px;
              padding: 0.5rem;
            }
          }
        }
      }
    }
  }
</style>

<script>
  import Tooltip from "../../components/legacy/Tooltip.svelte";
  import {
    heatmapData,
    heatmapOption,
    selectedHeatmapUser,
  } from "./utils/stores";

  const labelMapping = {
    clickTracking: "Clicks",
    openTracking: "Opens",
    replyTracking: "Replies",
  };

  // cutoff between switching from log to linear scale - chosen by looking at real heatmap data
  const logCutoffRatio = 0.3;

  // RGB values for the heatmap colors
  // BLUE Palette
  // - 10 discrete colours
  // - eaf1ff, d3e4ff, a2c9ff, 6baeff, 3593f1, 007dd9, 006dbe, 00599d, 003f72, 00315c
  const redArray = [234, 211, 162, 107, 53, 0, 0, 0, 0, 0];
  const greenArray = [241, 228, 201, 174, 147, 125, 109, 89, 63, 49];
  const blueArray = [255, 255, 255, 255, 241, 217, 190, 157, 114, 92];

  const endRGB = [0, 49, 92]; // default end color, last color in the palette

  let data = [];
  let reorderedData = [];
  let maxValue;
  let minValue;
  let RGBMapping = {};

  const getCellColor = (value) => {
    if (value === 0) {
      return "transparent";
    }
    const rgb = RGBMapping[value] || endRGB;
    return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  };

  const valFromArray = (array, t) => {
    // t is a value between 0 and 1
    // linearly interpolate between values in an array
    if (t === 0) {
      return array[0];
    }
    if (t === 1) {
      return array.at(-1);
    }
    const index = Math.floor(t * (array.length - 1));
    const deltaT = t * (array.length - 1) - index;

    return array[index] + deltaT * (array[index + 1] - array[index]);
  };

  const calculateRGB = (value, cutoff, logCutOff, logMinValue) => {
    const t =
      value <= cutoff
        ? ((cutoff / maxValue) * (Math.log10(value) - logMinValue)) /
          (logCutOff - logMinValue)
        : value / maxValue;

    return [
      valFromArray(redArray, t),
      valFromArray(greenArray, t),
      valFromArray(blueArray, t),
    ];
  };

  const calculateRGBMapping = (uniqueValues) => {
    const nonZeroValues = uniqueValues.filter((value) => value > 0);
    if (!nonZeroValues.length) {
      return;
    }

    maxValue = Math.max(...nonZeroValues);
    minValue = Math.min(...nonZeroValues);
    if (maxValue === 1 || minValue === maxValue) {
      RGBMapping = { [maxValue]: endRGB };
      return;
    }

    const cutoff = maxValue * logCutoffRatio;
    const logCutOff = Math.log10(cutoff);
    const logMinValue = Math.log10(minValue);

    nonZeroValues.forEach((value) => {
      RGBMapping[value] = calculateRGB(value, cutoff, logCutOff, logMinValue);
    });
  };

  $: {
    data =
      $heatmapData[$selectedHeatmapUser.value].heatmaps[$heatmapOption.value];

    // Reorder the data to start from Sunday
    reorderedData = [
      data[6], // Sunday
      ...data.slice(0, 6), // Monday to Saturday
    ];

    const flattenedValues = data.flatMap((day) =>
      day.data.map((hour) => hour.y)
    );
    const uniqueValues = [...new Set(flattenedValues)];

    calculateRGBMapping(uniqueValues);
  }
</script>

<div class="heatmap">
  <div class="heatmapGrid">
    {#each reorderedData as day, dayIndex}
      <div class="dayLabel">{day.name}</div>
      {#each day.data as hour, hourIndex}
        <div
          class="cell"
          class:notNull={!!hour.y}
          style="--cell-color: {getCellColor(hour.y)};"
        >
          {#if hour.y > 0}
            <Tooltip
              style="left: 0; position: absolute; top: 0; width: 100%; height: 100%;"
              yPlacement={dayIndex < 5 ? "bottom" : "top"}
              hoverHoldTime={null}
            >
              <span class="cellTooltipContent" slot="content">
                <h3>No. of {labelMapping[$heatmapOption.value]}</h3>
                <div class="subLine">
                  <div class="text">
                    <div class="icon"></div>
                    {day.name},
                    {#if hourIndex > 0}
                      {data[dayIndex].data[hourIndex - 1].x} -
                    {/if}
                    {hour.x}
                  </div>
                  {hour.y}
                </div>
              </span>
            </Tooltip>
          {/if}
        </div>
      {/each}
    {/each}
    <div id="cornerCell"></div>
    {#each reorderedData[0].data as hour}
      <div class="hourLabel">
        {hour.x}
      </div>
    {/each}
  </div>
</div>
